/**
 * Etichette e aritmetica dei bucket temporali dei costi.
 *
 * Regola che tiene in piedi tutto il modulo: le chiavi di bucket
 * (`bucket_start`, `bucket_end`, `date`) sono **ora locale di Roma, naive,
 * senza offset** — il fuso lo dichiara una volta la risposta, in `timezone`.
 * Quindi qui NON si costruisce mai un istante con `new Date("2026-09-07T14:00")`:
 * quella stringa viene letta come ora *del browser* e a ovest di Greenwich
 * sposta il giorno indietro (trappola gia' pagata in `AdminCosts.tsx` con
 * `splitDayKey`/`fmtDayKeyLong`, di cui queste funzioni sono la
 * generalizzazione). Si passa sempre da una regex + `Date.UTC(...)`, e la
 * formattazione usa `timeZone: "UTC"`: l'istante finto in UTC porta gli stessi
 * numeri del calendario locale, e il fuso della macchina non entra mai in gioco.
 *
 * Tutte le funzioni sono pure: nessun accesso a `window`, nessuna lettura di
 * `Date.now()`. L'"adesso" lo decide il backend troncando la serie.
 *
 * Nota ora legale: i bucket vivono sul calendario locale, come il
 * `date_trunc(... AT TIME ZONE 'Europe/Rome')` che li genera — anche il mese,
 * che parte dal primo giorno a mezzanotte italiana. Il 25 ottobre il
 * bucket `02:00` contiene due ore reali, il 29 marzo il bucket `02:00` non
 * esiste e non viene emesso. E' esatto rispetto all'unita' che l'utente legge
 * (l'orologio a muro), che e' lo stesso criterio scelto per i giorni.
 */

import type { CostGranularity } from "../api/types";

const RE_BUCKET_KEY = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/;

const MS_PER_DAY = 86_400_000;

/** Etichette del selettore di granularita' (`auto` incluso: e' un valore che si richiede). */
export const GRANULARITY_LABELS: Record<CostGranularity | "auto", string> = {
  auto: "Auto",
  hour: "Ore",
  day: "Giorni",
  week: "Settimane",
  month: "Mesi",
};

/**
 * Tetti del backend, in giorni di calendario, per granularita': oltre il
 * proprio tetto l'unita' e' un 400 (`_MAX_WINDOW_DAYS_BY_GRANULARITY` in
 * `src/api/routes/admin_costs.py`). Servono a NON offrire in UI una scelta che
 * la richiesta rifiuterebbe; la verita' resta del backend.
 */
const MAX_WINDOW_DAYS: Record<CostGranularity, number> = {
  hour: 7, // 168 bucket
  day: 400, // "un anno piu' il confronto"
  week: 1098, // tre anni = 157 bucket
  month: 3660, // il tetto assoluto della finestra: dieci anni = 120 bucket
};

/**
 * Chiave di bucket -> parti di calendario, senza passare da `new Date(stringa)`.
 *
 * Accetta sia `"YYYY-MM-DDTHH:MM"` (bucket orari) sia `"YYYY-MM-DD"` (campo
 * `date` e bucket giornalieri/settimanali), che vale mezzanotte locale.
 * Ritorna `null` su qualunque altra forma o su valori fuori calendario, cosi'
 * chi formatta puo' ricadere sulla stringa grezza invece di mostrare
 * "Invalid Date".
 */
export function splitBucketKey(
  s: string,
): { y: number; m: number; d: number; h: number; min: number } | null {
  const parts = RE_BUCKET_KEY.exec(s);
  if (!parts) return null;
  const y = Number(parts[1]);
  const m = Number(parts[2]);
  const d = Number(parts[3]);
  const h = parts[4] === undefined ? 0 : Number(parts[4]);
  const min = parts[5] === undefined ? 0 : Number(parts[5]);
  if (m < 1 || m > 12 || d < 1 || d > 31 || h > 23 || min > 59) return null;
  return { y, m, d, h, min };
}

/** Parti di calendario -> istante finto in UTC, l'unico su cui si fa aritmetica qui. */
function toUtcInstant(p: { y: number; m: number; d: number; h: number; min: number }): Date {
  return new Date(Date.UTC(p.y, p.m - 1, p.d, p.h, p.min));
}

/** Formatta un istante finto in UTC leggendolo in UTC: il fuso del browser non entra. */
function fmt(instant: Date, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("it-IT", { ...opts, timeZone: "UTC" }).format(instant);
}

/**
 * Giorni di calendario nella finestra, **estremi inclusi** (`date_from` ==
 * `date_to` -> 1). E' il numero su cui si risolve `auto` e si decidono le
 * granularita' offerte, quindi va contato sul calendario e non in ore: il
 * conto passa da `Date.UTC`, dove i giorni sono tutti di 86.400 s, percio' non
 * si perde (ne' si guadagna) un giorno a cavallo del cambio d'ora.
 *
 * Ritorna 0 se una delle due chiavi non e' un giorno valido, e un valore <= 0
 * se le date sono invertite (che per il backend e' un 400): non si corregge
 * l'input di nascosto.
 */
export function windowDays(dateFrom: string, dateTo: string): number {
  const a = splitBucketKey(dateFrom);
  const b = splitBucketKey(dateTo);
  if (!a || !b) return 0;
  const giorniInteri = Math.round(
    (toUtcInstant({ ...b, h: 0, min: 0 }).getTime() - toUtcInstant({ ...a, h: 0, min: 0 }).getTime()) /
      MS_PER_DAY,
  );
  return giorniInteri + 1;
}

/**
 * Risolve `auto` con le stesse soglie del backend (<=2 giorni -> ore, 3-92 ->
 * giorni, 93-366 -> settimane, oltre l'anno -> mesi). Una granularita'
 * esplicita si rispetta cosi' com'e': l'override dell'utente non si
 * sovrascrive.
 *
 * Le soglie sono duplicate rispetto al backend per necessita' (qui servono a
 * mostrare in anticipo che cosa si otterra'), ma la verita' resta la
 * `granularity` **risolta** che la risposta riporta: e' quella che il grafico
 * usa per le etichette, non questa previsione.
 */
export function resolveGranularity(
  days: number,
  requested: CostGranularity | "auto",
): CostGranularity {
  if (requested !== "auto") return requested;
  if (days <= 2) return "hour";
  if (days <= 92) return "day";
  if (days <= 366) return "week";
  return "month";
}

/**
 * Granularita' che il backend accetta per una finestra di `days` giorni, in
 * ordine dalla piu' fine: ognuna ha il suo tetto in giorni (`MAX_WINDOW_DAYS`),
 * perche' cio' che il tetto difende sono le barre e una barra dura un'ora o un
 * mese a seconda dell'unita'. `month` regge fino al tetto assoluto della
 * finestra, quindi c'e' sempre almeno una scelta possibile.
 */
export function granularityOptions(days: number): CostGranularity[] {
  const tutte: CostGranularity[] = ["hour", "day", "week", "month"];
  return tutte.filter((g) => days <= MAX_WINDOW_DAYS[g]);
}

/** Ultimo giorno (incluso) della settimana ISO che parte da `p`. */
function fineSettimana(p: { y: number; m: number; d: number; h: number; min: number }): Date {
  return new Date(toUtcInstant({ ...p, h: 0, min: 0 }).getTime() + 6 * MS_PER_DAY);
}

/**
 * Intervallo di giorni compatto, es. `"1–7 set"` dentro lo stesso mese e
 * `"29 set–5 ott"` a cavallo di due mesi (il mese si ripete solo quando cambia).
 */
function intervalloGiorni(inizio: Date, fine: Date, anno?: number): string {
  const gInizio = inizio.getUTCDate();
  const gFine = fine.getUTCDate();
  const mInizio = fmt(inizio, { month: "short" });
  const mFine = fmt(fine, { month: "short" });
  const coda = anno === undefined ? "" : ` ${anno}`;
  if (mInizio === mFine) return `${gInizio}–${gFine} ${mFine}${coda}`;
  return `${gInizio} ${mInizio}–${gFine} ${mFine}${coda}`;
}

/** `"HH:MM"` da una chiave di bucket, zero-padded. */
function oraDiChiave(p: { h: number; min: number }): string {
  return `${String(p.h).padStart(2, "0")}:${String(p.min).padStart(2, "0")}`;
}

/**
 * Etichetta breve per il tick dell'asse X, una per granularita':
 * `hour` -> `"14:00"`, `day` -> `"7/9"`, `week` -> `"1–7 set"`,
 * `month` -> `"set 2026"`.
 * Il mese porta l'anno anche nel tick, e non e' ridondanza: una finestra
 * mensile e' pluriennale per costruzione (sotto l'anno `auto` sta alle
 * settimane), quindi "set" da solo non direbbe *quale* settembre.
 * Su chiave malformata ricade sulla stringa grezza: meglio un tick strano di
 * un tick sbagliato.
 */
export function bucketTickLabel(bucketStart: string, g: CostGranularity): string {
  const p = splitBucketKey(bucketStart);
  if (!p) return bucketStart;
  if (g === "hour") return oraDiChiave(p);
  if (g === "day") return `${p.d}/${p.m}`;
  if (g === "month") {
    return fmt(toUtcInstant({ ...p, h: 0, min: 0 }), { month: "short", year: "numeric" });
  }
  return intervalloGiorni(toUtcInstant({ ...p, h: 0, min: 0 }), fineSettimana(p));
}

/**
 * Etichetta lunga per il tooltip:
 * `hour` -> `"lun 7 set, 14:00–15:00"` (l'ora di fine viene da `bucketEnd`,
 * che e' esclusivo: sul cambio d'ora e' il backend a sapere quanto dura
 * davvero il bucket, non noi);
 * `day` -> `"07 set 2026"`;
 * `week` -> `"settimana 1–7 set 2026"` (la settimana ISO si chiude sei giorni
 * dopo il lunedi' di `bucketStart`; un bucket parziale ai bordi della finestra
 * resta etichettato con la settimana intera, come lo e' il suo `bucket_start`);
 * `month` -> `"settembre 2026"`, il nome pieno del mese (nel tick c'e' quello
 * abbreviato).
 * Su chiave malformata ricade sulla stringa grezza.
 */
export function bucketTooltipLabel(
  bucketStart: string,
  bucketEnd: string,
  g: CostGranularity,
): string {
  const p = splitBucketKey(bucketStart);
  if (!p) return bucketStart;
  const inizio = toUtcInstant(p);

  if (g === "hour") {
    const fine = splitBucketKey(bucketEnd);
    const oraFine = fine
      ? oraDiChiave(fine)
      : oraDiChiave({ h: (p.h + 1) % 24, min: p.min });
    const giorno = fmt(inizio, { weekday: "short", day: "numeric", month: "short" });
    return `${giorno}, ${oraDiChiave(p)}–${oraFine}`;
  }

  if (g === "day") {
    return fmt(inizio, { day: "2-digit", month: "short", year: "numeric" });
  }

  if (g === "month") {
    return fmt(inizio, { month: "long", year: "numeric" });
  }

  const fineSett = fineSettimana(p);
  return `settimana ${intervalloGiorni(
    toUtcInstant({ ...p, h: 0, min: 0 }),
    fineSett,
    fineSett.getUTCFullYear(),
  )}`;
}
